/* eslint-disable eqeqeq */
import type { AppDispatch } from 'app/store';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { accessoryFormsApi, type PositionBase } from './accessoryFormsApi';
import { validateAccessoryOnChange } from '../../validation/validationController';

/**
 * Phase 6e2b-1 — THE position store (§51.3 / parity 332, 351). One class for
 * the 3 legacy position models (read in full):
 *  - accessories/accessory/model/position.js (urlRoot ahu/positions/ —
 *    generic; serves the on_cabinet shape when keyed by accessory id)
 *  - position.on_cabinet.js (ahu/positions/on_cabinet/)
 *  - position.on_internal_wall.js (ahu/positions/on_internal_walls/)
 *
 * Pipeline parity (position.js:42-84 / on_cabinet:43-78):
 *  - armed after the first sync (listenToOnce 'sync'); then every change:
 *  - `mounting_plane` echo STRIPPED from the change set (never PATCHed back);
 *  - plane-9 (fan_intake) crossing tracked across changes — entering OR
 *    leaving plane 9 broadcasts `damper:fan_intake:crossing(section_id, id)`
 *    AFTER the save succeeds (parity 351);
 *  - `no_fetch` flag (set by the positioningoncabinet socket handler, §49.6)
 *    swallows exactly ONE change cycle;
 *  - xhr/validate (generic) / patch/__from_back_end (on_cabinet) echoes never
 *    save;
 *  - save(self.changed, patch) → success: validateAccessoryOnChange(id) +
 *    door_ids partner sweep — SKIPPED when the single change is a
 *    default_pos_x/y un-check ("is_center") or mounting_type is
 *    on_internal_wall;
 *  - default_pos_x/y semantics live in the form (§49.6 onPositionFieldChange);
 *    the model only carries the is_center save-skip.
 *
 * DATA SAFETY (6e2a userArmed pattern — non-negotiable): position PATCHes are
 * STRUCTURALLY impossible until a real user gesture arms the model. Mount-time
 * fetches/sets are echo-flagged anyway; the gate makes the guarantee explicit.
 */

export interface PositionSetInfo {
  xhr?: boolean;
  validate?: boolean;
  __from_back_end?: boolean;
  silent?: boolean;
}

export class AccessoryPositionModel {
  readonly id: number;

  /** REST base — '' (generic), 'on_cabinet', 'on_internal_walls'. */
  base: PositionBase;

  /** legacy `mounting_type` ('on_cabinet' | 'on_internal_wall'). */
  mountingType: 'on_cabinet' | 'on_internal_wall';

  attributes: Record<string, unknown> = {};

  /** legacy `_section_id` stamp (crossing broadcast payload). */
  sectionId: number;

  /** legacy `position.door_ids` (partner-door validation sweep). */
  doorIds: Record<string, { section_id: number }> | null = null;

  /** §49.6 socket handler sets this — one change cycle swallowed. */
  no_fetch = false;

  /** 6e2a data-safety gate. */
  userArmed = false;

  private armed = false;

  private prevMountingPlaneId: number | null = null;

  private destroyed = false;

  private subscribers = new Set<() => void>();

  private version = 0;

  private dispatch: AppDispatch;

  /** Called after a successful PATCH. Set for a louver, whose every save --
   *  position and plane included -- can re-size or un-mount the damper mounted
   *  on it, server side (`AHUAccessoryLouver.reconcile_mounted_damper`). */
  private afterSave: (() => void) | null = null;

  constructor(args: {
    id: number;
    base: PositionBase;
    sectionId: number;
    dispatch: AppDispatch;
    initialAttributes?: Record<string, unknown>;
    afterSave?: () => void;
  }) {
    this.afterSave = args.afterSave ?? null;
    this.id = args.id;
    this.base = args.base;
    this.mountingType = args.base === 'on_internal_walls' ? 'on_internal_wall' : 'on_cabinet';
    this.sectionId = args.sectionId;
    this.dispatch = args.dispatch;
    if (args.initialAttributes) {
      this.attributes = { ...args.initialAttributes };
      this.armSync();
    }
  }

  markUserAction(): void {
    this.userArmed = true;
  }

  destroy(): void {
    this.destroyed = true;
    this.subscribers.clear();
  }

  subscribe = (cb: () => void): (() => void) => {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  };

  getVersion = (): number => this.version;

  get<T = unknown>(key: string): T {
    return this.attributes[key] as T;
  }

  toJSON(): Record<string, unknown> {
    return { ...this.attributes };
  }

  private bump(): void {
    this.version += 1;
    this.subscribers.forEach((cb) => cb());
  }

  /** listenToOnce('sync') — arm the change→save pipeline. */
  private armSync(): void {
    if (this.armed) return;
    this.armed = true;
    this.prevMountingPlaneId =
      this.attributes.mounting_plane_id != null
        ? Number(this.attributes.mounting_plane_id)
        : null;
  }

  /** GET (model.fetch()) — echo set, never a save. */
  async fetch(): Promise<Record<string, unknown> | null> {
    try {
      const row = await this.dispatch(
        accessoryFormsApi.endpoints.getPositionByBase.initiate(
          { base: this.base, id: this.id },
          { subscribe: false, forceRefetch: true }
        )
      ).unwrap();
      if (this.destroyed) return null;
      this.attributes = { ...this.attributes, ...row };
      if (this.attributes.mounting_plane_id != null) {
        this.prevMountingPlaneId = Number(this.attributes.mounting_plane_id);
      }
      this.armSync();
      this.bump();
      return row;
    } catch {
      return null;
    }
  }

  /**
   * Backbone set + the §51.3 change pipeline. Non-echo, armed changes save
   * `changed` via PATCH (the position model's own auto-save contract).
   */
  set(attrs: Record<string, unknown>, info: PositionSetInfo = {}): void {
    if (this.destroyed) return;
    const changed: Record<string, unknown> = {};
    Object.keys(attrs).forEach((key) => {
      if (this.attributes[key] !== attrs[key]) changed[key] = attrs[key];
      this.attributes[key] = attrs[key];
    });
    // mounting_plane echo strip (position.js:50 — the nested relation object
    // must never PATCH back).
    delete changed.mounting_plane;
    if (!Object.keys(changed).length) return;
    this.bump();
    if (info.silent) return;
    // 6e2b-2 — the legacy stateStorage bind broadcasts every position-model
    // change as `storage:accessory.position.model:change (model, changed)`;
    // the damper allow_fan_intake arbitration (§51.2) consumes it. Emitted
    // for every non-silent change exactly like the Backbone bind.
    ahuBoardBus.emit('storage:accessory.position.model:change', this, changed);
    if (!this.armed) return;

    // plane-9 crossing detection (position.js:52-59) — runs on EVERY change
    // event regardless of echo flags (legacy order).
    let crossing = false;
    if (Object.prototype.hasOwnProperty.call(changed, 'mounting_plane_id')) {
      const prevId = this.prevMountingPlaneId;
      const newId =
        this.attributes.mounting_plane_id != null
          ? Number(this.attributes.mounting_plane_id)
          : null;
      this.prevMountingPlaneId = newId;
      if ((prevId === 9) !== (newId === 9)) crossing = true;
    }

    if (info.validate || info.xhr || info.__from_back_end) return;
    if (this.no_fetch) {
      // single-cycle swallow (§51.3 / parity 351).
      this.no_fetch = false;
      return;
    }
    // DATA SAFETY: no PATCH until a user gesture armed this model.
    if (!this.userArmed) return;

    const length = Object.keys(changed).length;
    const isCenter =
      (Object.prototype.hasOwnProperty.call(changed, 'default_pos_y') && !changed.default_pos_y) ||
      (Object.prototype.hasOwnProperty.call(changed, 'default_pos_x') && !changed.default_pos_x);

    void this.save(changed, { length, isCenter, crossing });
  }

  /** PATCH `changed` (position.js:68-83 save block). */
  async save(
    data: Record<string, unknown>,
    opts: { length?: number; isCenter?: boolean; crossing?: boolean } = {}
  ): Promise<Record<string, unknown> | null> {
    try {
      const res = await this.dispatch(
        accessoryFormsApi.endpoints.patchPositionByBase.initiate({
          base: this.base,
          id: this.id,
          data,
        })
      ).unwrap();
      if (this.destroyed) return res;
      this.attributes = { ...this.attributes, ...res };
      delete (this.attributes as Record<string, unknown>).mounting_plane;
      this.bump();
      this.afterSave?.();
      // success cascade (position.js:71-78).
      const skipValidate =
        (opts.length === 1 && opts.isCenter) || this.mountingType === 'on_internal_wall';
      if (!skipValidate) {
        void this.dispatch(validateAccessoryOnChange(this.id));
        if (this.doorIds) {
          Object.keys(this.doorIds).forEach((id) =>
            this.dispatch(validateAccessoryOnChange(Number(id)))
          );
        }
        if (opts.crossing) {
          ahuBoardBus.emit('damper:fan_intake:crossing', this.sectionId, this.id);
        }
      }
      return res;
    } catch {
      // legacy error handler: console.log('error') — no rollback.
      return null;
    }
  }
}
